import React, { ReactElement, useEffect, useState, WeakValidationMap } from 'react';
import PropTypes, { Validator } from 'prop-types';
import { forgeClassHelper } from '../utils/classes';
import FakeEvent from '../utils/fake-event';
import forwardRefToProps from '../utils/forwardRefToProps';
import { SimpleSpread } from '../utils/types';

const classes = forgeClassHelper({ name: 'toggle-switch' });

export type ToggleSwitchDescriptionPosition = 'left' | 'right';
export type ToggleSwitchSize = 'medium' | 'large';

/** Provides a Typescript error if checked and defaultChecked are present
 * at the same time.
 */
export type ToggleSwitchCheckedProps =
  | {
      /**
       * Sets the ToggleSwitch to be checked or unchecked.
       * Setting this property makes this a
       * [controlled](https://reactjs.org/docs/forms.html#controlled-components) component
       */
      checked?: boolean;
      defaultChecked?: never;
    }
  | {
      checked?: never;
      /** Sets the initial checked/unchecked state of the switch, while allowing
       * it to be changed by the user
       * ([uncontrolled](https://reactjs.org/docs/uncontrolled-components.html)).*/
      defaultChecked?: boolean;
    };

/** Simplified version of ToggleSwitchCheckedProps, compatible with prop-types */
export interface ToggleSwitchSimpleCheckedProps {
  checked?: boolean;
  defaultChecked?: boolean;
}

export interface ToggleSwitchBaseProps {
  /** Adds a class to the root element of the component */
  className?: string;
  /** Sets the position of the description of the ToggleSwitch */
  descriptionPosition?: ToggleSwitchDescriptionPosition;
  /** Provides labels for each state */
  descriptions?: {
    /** String used to accessibly label the toggle button */
    id: string;
    /** Descriptive label for checked state */
    checked: string;
    /** Descriptive label for unchecked state */
    unchecked: string;
  };
  /** Sets the id attribute on the native `<button>` */
  id?: string;
  /** Sets the ToggleSwitch disabled/enabled */
  disabled?: boolean;
  /** Setting this prop gives the ToggleSwitch 'error' styling */
  error?: string;
  /** Callback on ToggleSwitch blur */
  onBlur?: (event: FakeEvent<boolean>) => void;
  /** Callback on ToggleSwitch checked state changed */
  onChange?: (event: FakeEvent<boolean>) => void;
  /** Sets the size of the switch. Recommendation is to always use the 'large' on mobile. */
  size?: ToggleSwitchSize;
  /** Sets the value attribute on the native `<button>` */
  valueAttribute?: string;
  /** Ignored props forwarded from FormField
   *
   *
   * ToggleSwitch doesn't implement styling for `required` props, so there is no need to ignore styling
   */
  hideRequiredStyles?: boolean;
}

/** Props for ToggleSwitch */
export type ToggleSwitchProps = SimpleSpread<
  React.ButtonHTMLAttributes<HTMLElement>,
  ToggleSwitchBaseProps & ToggleSwitchCheckedProps
>;

/** Props used by the implementation of ToggleSwitch
 *
 * Adds forwardRef, which is not present in the props of the default export.
 */
export type ToggleSwitchComponentProps = ToggleSwitchProps & {
  /** Ref forwarded to ToggleSwitch's button element */
  forwardedRef?: React.Ref<HTMLButtonElement>;
};

/** Used to validate that prop-types matches ToggleSwitchComponentProps,
 * after flattening union types */
export type ToggleSwitchPropTypes = SimpleSpread<ToggleSwitchComponentProps, ToggleSwitchSimpleCheckedProps>;

function ToggleSwitchComponent({
  checked: upstreamChecked,
  className,
  defaultChecked = false,
  defaultValue,
  descriptionPosition = 'right',
  descriptions,
  disabled = false,
  error,
  forwardedRef,
  hideRequiredStyles,
  id,
  onBlur,
  onChange,
  size = 'medium',
  value,
  valueAttribute,
  'aria-labelledby': ariaLabelledby,
  ...passedProps
}: ToggleSwitchComponentProps): ReactElement {
  const [checked, setChecked] = useState(Boolean(upstreamChecked ?? value ?? defaultValue ?? defaultChecked));
  const isInControlledMode = upstreamChecked !== undefined || value !== undefined;

  useEffect(() => {
    if (isInControlledMode) {
      setChecked(Boolean(upstreamChecked ?? value));
    }
  }, [upstreamChecked, value, isInControlledMode]);

  function handleBlur(): void {
    if (onBlur) {
      onBlur(new FakeEvent({ value: checked, id }));
    }
  }

  function toggleChecked(): void {
    if (!disabled) {
      if (!isInControlledMode) {
        setChecked((p) => !p);
      }
      if (onChange) {
        onChange(new FakeEvent({ value: !checked, id }));
      }
    }
  }

  function getCombinedAriaLabelBy(): string {
    if (ariaLabelledby && descriptions) return `${ariaLabelledby} ${descriptions.id}`;
    if (descriptions) return descriptions.id;
    if (ariaLabelledby) return ariaLabelledby;
    return '';
  }

  const combinedAriaLabelledby = getCombinedAriaLabelBy();
  const toggleValue = valueAttribute ? { value: valueAttribute } : null;

  const useErrorStyles = error && !disabled;

  return (
    <div
      {...classes({
        modifiers: [descriptionPosition === 'left' ? 'left-description' : '', size === 'large' ? 'large' : ''],
        extra: className,
      })}
    >
      <button
        aria-checked={checked}
        aria-disabled={disabled}
        aria-labelledby={combinedAriaLabelledby}
        id={id}
        disabled={disabled}
        onClick={toggleChecked}
        onBlur={handleBlur}
        role="switch"
        ref={forwardedRef}
        type="button"
        {...toggleValue}
        {...classes({
          element: 'track',
          states: {
            checked,
            disabled,
            error: !!useErrorStyles,
          },
          modifiers: [size === 'large' ? 'large' : ''],
        })}
        {...passedProps}
      >
        <div
          {...classes({
            element: 'toggle',
            states: {
              checked,
              disabled,
              error: !!useErrorStyles,
            },
            modifiers: [size === 'large' ? 'large' : ''],
          })}
        />
      </button>
      {descriptions && (
        <label
          id={descriptions.id}
          {...classes({
            element: 'descriptions',
            states: {
              checked,
              disabled,
              error: !!useErrorStyles,
            },
            modifiers: [descriptionPosition === 'left' ? 'left' : '', size === 'large' ? 'large' : ''],
          })}
        >
          {checked ? descriptions.checked : descriptions.unchecked}
        </label>
      )}
    </div>
  );
  /* eslint-enable */
}

const toggleSwitchPropTypes: WeakValidationMap<ToggleSwitchPropTypes & { '...rest': unknown }> = {
  /** Specify an additional element ID to label the input. (The input is
   * automatically labeled by the input label, so specifying this additional
   * label target merely provides an additional label.) */
  'aria-labelledby': PropTypes.string,
  /**
   * Sets the ToggleSwitch to be checked or unchecked.
   * Setting this property makes this a
   * [controlled](https://reactjs.org/docs/forms.html#controlled-components) component
   */
  checked: PropTypes.bool,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Sets the initial checked/unchecked state of the switch, while allowing it
   * to be changed by the user
   * ([uncontrolled](https://reactjs.org/docs/uncontrolled-components.html)).*/
  defaultChecked: PropTypes.bool,
  /** Sets the position of the description of the ToggleSwitch */
  descriptionPosition: PropTypes.oneOf(['left', 'right']),
  /** Provides labels for each state
   *
   * * id: String used to accessibly label the toggle button
   * * checked: Descriptive label for checked state
   * * unchecked: Descriptive label for unchecked state
   */
  descriptions: PropTypes.shape({
    /** Used to accessibly label the toggle button */
    id: PropTypes.string.isRequired,
    /** Descriptive label for checked state */
    checked: PropTypes.string.isRequired,
    /** Descriptive label for unchecked state */
    unchecked: PropTypes.string.isRequired,
  }),
  /** Sets the ToggleSwitch disabled/enabled */
  disabled: PropTypes.bool,
  /** Setting this prop gives the ToggleSwitch 'error' styling */
  error: PropTypes.string,
  /** Sets the id attribute on the native `<button>` */
  id: PropTypes.string,
  /** Callback on ToggleSwitch blur */
  onBlur: PropTypes.func,
  /** Callback on ToggleSwitch checked state changed */
  onChange: PropTypes.func,
  /** Ref forwarded to ToggleSwitch's button element */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    /** Note: { current: PropTypes.instanceOf(HTMLButtonElement) } would be more
     * correct, but causes a StackOverflow when building gatsby.
     */
    PropTypes.shape({ current: PropTypes.any }),
  ]) as Validator<React.Ref<HTMLButtonElement>>,
  /** Sets the size of the switch. Recommendation is to always use the 'large' on mobile. */
  size: PropTypes.oneOf(['medium', 'large']),
  /** Sets the value attribute on the native `<button>` */
  valueAttribute: PropTypes.string,
  /** Passthrough props to the root <div> element */
  '...rest': PropTypes.any,
};

ToggleSwitchComponent.propTypes = toggleSwitchPropTypes;

ToggleSwitchComponent.displayName = 'ToggleSwitch';

export default forwardRefToProps(ToggleSwitchComponent);
